import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/hooks/use-confirm";
import { BomItemPicker } from "./BomItemPicker";
import type { BomItemSummary } from "@/lib/bom-items";
import {
  blankTemplateLineItem,
  type TemplateLineItem,
} from "@/lib/template-line-items";

export interface TemplateLineItemsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialItems: TemplateLineItem[];
  onDone: (items: TemplateLineItem[]) => void;
}

export function TemplateLineItemsEditor({
  open,
  onOpenChange,
  initialItems,
  onDone,
}: TemplateLineItemsEditorProps) {
  const [items, setItems] = useState<TemplateLineItem[]>(initialItems);
  const [picking, setPicking] = useState(false);
  const confirm = useConfirm();

  // Reseed the draft whenever the modal (re)opens. Callers MUST pass a stable
  // `initialItems` reference (useState/useMemo) while the modal is open — a new
  // array identity mid-edit would wipe in-progress edits.
  useEffect(() => {
    if (open) setItems(initialItems);
  }, [open, initialItems]);

  function patch(i: number, p: Partial<TemplateLineItem>) {
    setItems((cur) => cur.map((it, idx) => (idx === i ? { ...it, ...p } : it)));
  }

  async function removeRow(i: number) {
    if (items.length === 1) {
      const ok = await confirm({
        title: "Remove the last line item?",
        body: "The template will have no line items.",
        confirmLabel: "Delete",
        intent: "destructive",
      });
      if (!ok) return;
    }
    setItems((cur) => cur.filter((_, idx) => idx !== i));
  }

  function move(i: number, delta: number) {
    const j = i + delta;
    setItems((cur) => {
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  function pick(master: BomItemSummary) {
    setItems((cur) => [
      ...cur,
      {
        ...blankTemplateLineItem(),
        itemId: master.bomItemId,
        itemName: master.name,
        calcType: master.calcType,
        calcFactor: master.calcFactor,
        unitCost: master.unitCost,
        unitCurrencyCode: master.unitCurrencyCode,
        priceByRange: master.priceByRange,
        unitCostByRangeJson: master.unitCostByRangeJson,
        supplierId: master.supplierId,
        itemiseOnQuote: master.itemiseOnQuote,
        note: master.note,
      },
    ]);
    setPicking(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit line items</DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No line items — add one below.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th scope="col" className="px-2 py-1 text-left font-medium">Item</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">Calc</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">Factor</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">Unit cost</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">Note</th>
                  <th scope="col" className="px-2 py-1"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="px-2 py-1">
                      <Input
                        aria-label={`Item name for row ${i + 1}`}
                        value={it.itemName ?? ""}
                        onChange={(e) => patch(i, { itemName: e.target.value === "" ? null : e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        aria-label={`Calc type for row ${i + 1}`}
                        value={it.calcType ?? ""}
                        onChange={(e) => patch(i, { calcType: e.target.value === "" ? null : e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number" step="any" aria-label={`Calc factor for row ${i + 1}`}
                        value={it.calcFactor ?? ""}
                        onChange={(e) => patch(i, { calcFactor: e.target.value === "" ? null : Number(e.target.value) })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      {it.priceByRange ? (
                        <span className="inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          range-priced
                        </span>
                      ) : (
                        <Input
                          type="number" step="0.01" aria-label={`Unit cost for row ${i + 1}`}
                          value={it.unitCost ?? ""}
                          onChange={(e) => patch(i, { unitCost: e.target.value === "" ? null : Number(e.target.value) })}
                        />
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        aria-label={`Note for row ${i + 1}`}
                        value={it.note ?? ""}
                        onChange={(e) => patch(i, { note: e.target.value === "" ? null : e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center">
                        <Button type="button" variant="ghost" size="icon"
                          aria-label={`Move row ${i + 1} up`} disabled={i === 0}
                          onClick={() => move(i, -1)}>
                          <ChevronUp className="size-4" aria-hidden="true" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon"
                          aria-label={`Move row ${i + 1} down`} disabled={i === items.length - 1}
                          onClick={() => move(i, +1)}>
                          <ChevronDown className="size-4" aria-hidden="true" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon"
                          aria-label={`Remove row ${i + 1}`}
                          onClick={() => void removeRow(i)}>
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {picking ? (
            <BomItemPicker onPick={pick} onCancel={() => setPicking(false)} />
          ) : (
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm"
                onClick={() => setItems((cur) => [...cur, blankTemplateLineItem()])}>
                Add blank row
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
                Add from catalogue
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onDone(items)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
